import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, User, ShieldCheck, Volume2, VolumeX, ChevronRight, RotateCcw } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { useSoundPref } from "../hooks/useSoundPref";
import { playBreathChime } from "../lib/audio";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [soundOn, setSoundOn] = useSoundPref();
  const [resetting, setResetting] = useState(false);

  async function doLogout() {
    await logout();
    navigate("/login");
  }

  function toggleSound(next) {
    setSoundOn(next);
    if (next) {
      // play a tiny preview chime so the user hears the effect immediately
      setTimeout(() => playBreathChime("INHALE"), 60);
    }
  }

  async function doReset() {
    setResetting(true);
    try {
      const { data } = await api.post("/me/reset-progress");
      const cleared = data.cleared || {};
      const total = Object.values(cleared).reduce((a, b) => a + b, 0);
      toast.success("Course reset.", { description: `Cleared ${total} entries. You're back to Week 1.` });
      navigate("/journey");
    } catch (e) {
      toast.error("Could not reset.", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setResetting(false);
    }
  }

  return (
    <Layout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="bps-eyebrow">Settings</div>
        <h1 className="font-display text-3xl font-light tracking-tighter mt-1">Your account</h1>

        <div className="mt-6 bps-card p-5 flex items-center gap-4" data-testid="account-card">
          <div className="w-14 h-14 rounded-full bg-[#22D3EE]/15 flex items-center justify-center">
            <User size={22} className="text-[#22D3EE]" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg">{user?.name}</div>
            <div className="text-xs text-[#9AAEC6]">{user?.email}</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8FA8]">{user?.role}</div>
        </div>

        <div className="mt-4 bps-card p-5 flex items-center gap-4" data-testid="sound-settings">
          <div className="w-14 h-14 rounded-full bg-[#22D3EE]/15 flex items-center justify-center shrink-0">
            {soundOn ? (
              <Volume2 size={22} className="text-[#22D3EE]" strokeWidth={1.6} />
            ) : (
              <VolumeX size={22} className="text-[#7C8FA8]" strokeWidth={1.6} />
            )}
          </div>
          <div className="flex-1">
            <div className="font-display text-base text-white">Breath chimes</div>
            <div className="text-xs text-[#9AAEC6] mt-0.5 leading-snug">
              Soft tones at each phase of the 4–4–8 breathwork — guide your practice with eyes closed.
            </div>
          </div>
          <Switch
            checked={soundOn}
            onCheckedChange={toggleSound}
            data-testid="sound-toggle"
            className="data-[state=checked]:bg-[#22D3EE]"
          />
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              data-testid="restart-course-trigger"
              className="mt-4 w-full bps-card p-5 flex items-center gap-4 text-left hover:-translate-y-0.5 hover:border-[#F59E0B]/30 transition-all group"
            >
              <div className="w-14 h-14 rounded-full bg-[#F59E0B]/15 flex items-center justify-center shrink-0">
                <RotateCcw size={22} className="text-[#F59E0B]" strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <div className="font-display text-base text-white">Start from the beginning</div>
                <div className="text-xs text-[#9AAEC6] mt-0.5 leading-snug">
                  Erase all your data and start completely over from Week 1.
                </div>
              </div>
              <ChevronRight size={18} className="text-[#7C8FA8] group-hover:text-[#F59E0B] transition-colors" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="border border-[#22D3EE]/30 bg-[#0A1530]/95 backdrop-blur-xl rounded-3xl shadow-[0_24px_60px_rgba(0,0,0,0.6),0_0_0_1px_rgba(34,211,238,0.08)] max-w-sm" data-testid="restart-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl font-light tracking-tighter text-white">
                Start the course over?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-[#9AAEC6] leading-relaxed">
                This will erase <span className="text-white">everything</span> — surveys, body scans, video progress, journal entries, reflections, daily check-ins, 7-day practice completions, and flare logs — and unlock Week 1 again from a blank slate.
                <br /><br />
                Only your <span className="text-white">account login</span> stays so you can sign back in.
                <br /><br />
                <span className="text-[#F87171]">This cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-2 mt-2">
              <AlertDialogCancel data-testid="restart-cancel" className="bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-full">
                Keep my progress
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="restart-confirm"
                onClick={doReset}
                disabled={resetting}
                className="bg-[#22D3EE] text-[#03101F] hover:bg-[#0AB7D6] rounded-full font-semibold shadow-[0_0_18px_rgba(34,211,238,0.35)]"
              >
                {resetting ? "Resetting…" : "Yes, restart"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="mt-4 bps-card p-5 space-y-3">
          <div className="bps-eyebrow">About</div>
          <p className="text-sm text-white leading-relaxed">
            <span className="font-display font-medium">Back Pain Solution</span> is a guided 4-week mind–body
            program. It pairs nervous-system awareness with{" "}
            <span className="font-medium">The Back Pain Transformer</span>, the full-body harness designed
            to support alignment and posture between sessions.
          </p>
          <div className="text-xs text-[#7C8FA8] flex items-center gap-2"><ShieldCheck size={14} /> Your reflections are private to you.</div>
        </div>

        <button onClick={doLogout} data-testid="logout-button" className="mt-6 w-full py-4 rounded-full bg-white/5 text-white font-medium hover:bg-white/10 transition-all inline-flex items-center justify-center gap-2">
          <LogOut size={16} /> Sign out
        </button>
      </motion.div>
    </Layout>
  );
}
